import { createTheme } from '@mui/material/styles';

// 🎨 Define All Color Variables at the Top for Easy Customization
const colorVariables = {
	// ⬇️ Primary Text Color is now #d1d1d1 (even less harsh on dark background)
	textPrimary: '#d1d1d1',

	primary: '#42a5f5',
	primaryLight: '#69b3ff',
	primaryDark: '#1e88e5',
	primaryContrastText: '#ffffff',
	secondary: '#f50057',
	secondaryLight: '#ff66bf',
	secondaryDark: '#c40042',
	secondaryContrastText: '#ffffff',
	error: '#e53935',
	warning: '#fdd835',
	info: '#00bcd4',
	success: '#4caf50',

	// Backgrounds - Dark Mode
	backgroundDefault: '#121212',
	backgroundPaper: '#1e1e1e',

	// Additional theme colors
	footerBackground: '#212121',
	overlayColor: 'rgba(0, 0, 0, 0.03)',
	borderColor: 'rgba(0,0,0,0.20)',

	// Text Colors for Dark Mode
	textSecondary: '#b0b0b0',
	textDisabled: '#666666'
};

// ✅ Apply Google Font "Outfit"
const OutfitFont = '"Outfit", sans-serif';

// 🎯 Theme Configuration with Elevation Disabled - DASHBOARD DARK MODE
const darkTheme = createTheme({
	palette: {
		mode: 'dark',

		primary: {
			main: colorVariables.primary,
			light: colorVariables.primaryLight,
			dark: colorVariables.primaryDark,
			contrastText: colorVariables.primaryContrastText
		},

		secondary: {
			main: colorVariables.secondary,
			light: colorVariables.secondaryLight,
			dark: colorVariables.secondaryDark,
			contrastText: colorVariables.secondaryContrastText
		},

		error: {
			main: colorVariables.error,
			contrastText: '#fff'
		},

		warning: {
			main: colorVariables.warning,
			contrastText: '#333'
		},

		info: {
			main: colorVariables.info,
			contrastText: '#333'
		},

		success: {
			main: colorVariables.success,
			contrastText: '#fff'
		},

		background: {
			default: colorVariables.backgroundDefault,
			paper: colorVariables.backgroundPaper
		},

		text: {
			primary: colorVariables.textPrimary, // Uses your new softer value (less harsh)
			secondary: colorVariables.textSecondary,
			disabled: colorVariables.textDisabled
		}
	},

	typography: {
		fontFamily: OutfitFont,
		fontWeightLight: 300,
		fontWeightRegular: 400,
		fontWeightMedium: 500,
		fontWeightBold: 700,

		h1: { fontSize: '3rem', letterSpacing: '.05em' },
		h2: { fontSize: '2.5rem' },
		h3: { fontSize: '2rem' },
		h4: { fontSize: '1.75rem' },
		h5: { fontSize: '1.5rem' },
		h6: { fontSize: '1.25rem' },

		body1: { fontSize: '1rem' },
		button: { textTransform: 'none' },
		caption: { fontSize: '0.75rem' }
	},

	shape: { borderRadius: 8 },

	shadows: [
		// Reduced shadows for dark mode (more subtle)
		'none',
		'rgba(0,0,0,0.3)',
		'rgba(0,0,0,0.4)'
	],

	components: {
		MuiButton: {
			styleOverrides: {
				root: {
					textTransform: 'none',
					borderRadius: 8, // ✅ Explicitly set to 8
					boxShadow: 'none', // ✅ Disable button shadows
					fontWeight: 500
				},
				contained: { boxShadow: 'none' },
				outlined: {
					borderWidth: 1,
					boxShadow: 'none'
				}
			}
		},

		MuiPaper: {
			styleOverrides: {
				root: { boxShadow: 'none' } // ✅ Disable paper shadows
			}
		},

		MuiCard: {
			styleOverrides: {
				root: { boxShadow: 'none' } // ✅ Card inherits from Paper
			}
		},

		MuiTableCell: {
			styleOverrides: {
				root: {
					boxShadow: 'none',
					'&.elevation-0': {}
				}
			}
		},

		MuiList: {
			styleOverrides: {
				root: {
					boxShadow: 'none'
				}
			}
		},

		// ✅ Remove background color for all labels (TextField/FormControlLabel)
		MuiInputLabel: {
			styleOverrides: {
				root: {
					backgroundColor: 'transparent', // ✅ Transparent label backgrounds
					color: 'inherit'
				}
			}
		},

		MuiInputBase: {
			styleOverrides: {
				root: {
					backgroundColor: 'transparent', // ✅ Transparent input backgrounds
					'& fieldset': { borderColor: 'transparent' } // ✅ Remove border if applicable
				}
			}
		},

		MuiFormControl: {
			styleOverrides: {
				root: {
					backgroundColor: 'transparent', // ✅ Clear any control backgrounds
					'& .MuiInputLabel-root': { backgroundColor: 'transparent' } // ✅ Label transparency
				}
			}
		},

		MuiFormLabel: {
			styleOverrides: {
				root: {
					backgroundColor: 'transparent' // ✅ Form label transparency
				}
			}
		},

		// Add these to remove unwanted background fills from labels/typography containers
		MuiTypography: {
			styleOverrides: {
				root: {
					backgroundColor: 'transparent', // ✅ All typography elements transparent by default
					'&.Mui-selected': { backgroundColor: 'transparent' } // ✅ Selected state also transparent
				}
			}
		},

		MuiAccordionDetails: {
			styleOverrides: {
				root: {
					backgroundColor: 'transparent' // ✅ Accordion content transparent
				}
			}
		},

		MuiMenu: {
			styleOverrides: {
				list: {
					backgroundColor: 'transparent' // ✅ Menu items transparent background
				}
			}
		},

		// ✅ NEW: Remove ListSubheader background color
		MuiListSubheader: {
			styleOverrides: {
				root: {
					backgroundColor: 'transparent', // ✅ Subheader now has no background fill
					'&.Mui-selected': { backgroundColor: 'transparent' } // ✅ Selected state also transparent
				}
			}
		}
	}
});

export default darkTheme;
